import React, { useState } from 'react';

import { TFolderId } from '@widgets/Files/model/types/folderId.type';

interface IUseDnd {
    mode: 'innerFiles' | 'allFiles' | 'folder';
    onFileChange?: (file: File[], currentFolderId: TFolderId) => void;
    acceptDrop?: (e: React.DragEvent<HTMLDivElement>) => boolean;
    currentFolderId: TFolderId;
}

export const useDnd = ({ mode, onFileChange, acceptDrop, currentFolderId }: IUseDnd) => {
    const [isFileOver, setIsFileOver] = useState(false);

    const shouldAccept = (e: React.DragEvent<HTMLDivElement>) => {
        if (acceptDrop) {
            return acceptDrop(e);
        }
        return true;
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();

        if (!shouldAccept(e)) return;

        if (mode === 'allFiles' && e.dataTransfer.types.includes('Files')) {
            setIsFileOver(true);
            return;
        }
        setIsFileOver(true);
    };

    const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
        const target = (e.target as HTMLElement).closest('[data-file-id]') as HTMLElement | null;
        const fileId = target?.dataset.fileId;

        if (fileId) {
            e.dataTransfer.setData('text/internal-file', fileId);
        }
    };

    const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();

        if (!shouldAccept(e)) return;

        if (mode === 'allFiles' && e.dataTransfer.types.includes('Files')) {
            setIsFileOver(true);
            return;
        }
        setIsFileOver(true);
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const { relatedTarget } = e;

        if (!e.currentTarget.contains(relatedTarget as Node)) {
            setIsFileOver(false);
        }
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
        e.preventDefault();
        setIsFileOver(false);

        const isInternal = e.dataTransfer.types.includes('text/internal-file');

        if (isInternal) {
            return;
        }

        if (!shouldAccept(e)) return;

        const fileList = e.dataTransfer?.files;
        if (!fileList) return;

        const files: File[] = Array.from(fileList);
        if (files.length > 0) {
            onFileChange?.(files, currentFolderId);
        }
    };

    return {
        isFileOver,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDragStart,
        handleDrop,
        setIsFileOver,
    };
};
